import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Calendar, MapPin, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import FavoriteButton from '../components/FavoriteButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Favoritos = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`${API}/users/me/favorites`, {
          withCredentials: true,
        });
        setEvents(response.data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    load();
    // Reload if the user's favorites list changes (e.g., after un-favoriting on this page)
  }, [user?.favorites?.length]);

  return (
    <div className="min-h-screen bg-background">
      {/* Dark hero */}
      <section className="bg-[#0A0A0A] text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="favorites-hero">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Heart className="text-white w-7 h-7 fill-current" />
          </div>
          <div>
            <span className="inline-block bg-white/10 text-white text-xs font-bold tracking-widest px-2 py-1 rounded mb-2">
              MIS FAVORITOS
            </span>
            <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Tu próximo plan</h1>
            <p className="text-white/70 mt-1 text-sm">
              {loading
                ? 'Cargando...'
                : events.length === 0
                ? 'Guardá los eventos que te interesan para no perderlos.'
                : `${events.length} evento${events.length === 1 ? '' : 's'} guardado${events.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-white/60 rounded-lg h-64"></div>
              ))}
            </div>
          )}

          {!loading && events.length === 0 && (
            <div
              className="border-2 border-dashed border-[#0A0A0A]/15 rounded-lg p-14 text-center bg-white"
              data-testid="favorites-empty"
            >
              <Heart className="w-14 h-14 text-[#0A0A0A]/30 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-[#0A0A0A] mb-2">
                Todavía no tenés eventos guardados
              </h2>
              <p className="text-[#525252] mb-6 max-w-md mx-auto">
                Tocá el corazón <Heart className="inline w-4 h-4 mx-1 text-primary" /> en cualquier
                evento para guardarlo acá y volver rápido.
              </p>
              <Link to="/explorar">
                <Button className="bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white font-semibold">
                  <Compass className="w-4 h-4 mr-2" />
                  Explorar eventos
                </Button>
              </Link>
            </div>
          )}

          {!loading && events.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
              {events.map((event) => (
                <FavoriteEventCard key={event.event_id} event={event} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const FavoriteEventCard = ({ event }) => {
  const past = event.end_date && new Date(event.end_date) < new Date();
  return (
    <Link
      to={`/eventos/${event.event_id}`}
      className="group relative block bg-white rounded-lg overflow-hidden border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
      data-testid={`favorite-card-${event.event_id}`}
    >
      <FavoriteButton eventId={event.event_id} variant="overlay" />
      <div className="aspect-video bg-[#F5F5F5] overflow-hidden">
        {event.images?.[0] ? (
          <img
            src={event.images[0]}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#A3A3A3]">Sin imagen</div>
        )}
      </div>
      <div className="p-4 space-y-2">
        {past && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0A0A0A] text-white px-2 py-0.5 rounded">
            Finalizado
          </span>
        )}
        <h3 className="font-heading font-bold text-[#0A0A0A] line-clamp-2">{event.title}</h3>
        <div className="flex items-center gap-2 text-sm text-[#525252]">
          <Calendar className="w-4 h-4 text-primary" />
          {format(new Date(event.start_date), "d MMM · HH:mm", { locale: es })} hs
        </div>
        {event.location && (
          <div className="flex items-start gap-2 text-sm text-[#525252]">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span className="line-clamp-1">
              {event.location.venue_name || event.location.address}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default Favoritos;
